import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Megaphone, Loader2, Plus, Trash2, Edit2, Calendar, Target, Share2, Palette,
  Image as ImageIcon, Users, Newspaper, PartyPopper, LineChart as ChartIcon,
  Download, Upload, ShieldCheck, AlertTriangle, CheckCircle2
} from 'lucide-react';
import SocialTab from './marketing/SocialTab';

const TAB_META = [
  { key: 'scorecard', label: 'Scorecard', icon: ChartIcon },
  { key: 'editorial', label: 'Editorial', icon: Calendar },
  { key: 'campaigns', label: 'Campaigns', icon: Target },
  { key: 'social', label: 'Social', icon: Share2 },
  { key: 'assets', label: 'Brand Assets', icon: Palette },
  { key: 'media', label: 'Media Library', icon: ImageIcon },
  { key: 'partners', label: 'Partners', icon: Users },
  { key: 'press', label: 'Press', icon: Newspaper },
  { key: 'events', label: 'Events', icon: PartyPopper },
];

const CONTENT_STATUSES = ['idea', 'draft', 'review', 'approved', 'published', 'archived'];
const CHANNELS = ['blog', 'social', 'newsletter', 'press_release', 'video', 'other'];
const STATUS_COLOR = {
  idea: 'bg-gray-100 text-gray-700',
  draft: 'bg-[#FFF3E0] text-[#E65100]',
  review: 'bg-[#E1F5FE] text-[#0277BD]',
  approved: 'bg-[#F3E5F5] text-[#4A148C]',
  published: 'bg-[#E8F5E9] text-[#1B5E20]',
  archived: 'bg-gray-100 text-gray-500',
};

const fmt$ = n => `$${(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`;

export default function MarketingPage() {
  const { api } = useAuth();
  const [tab, setTab] = useState('scorecard');

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="marketing-page">
      <div className="flex items-center gap-3 mb-1">
        <Megaphone className="h-8 w-8 text-[#E91E63]" />
        <div>
          <h1 className="text-4xl font-bold">Marketing</h1>
          <p className="text-gray-500 text-sm">Confidential — access granted per user in Settings → Tools</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b my-4">
        {TAB_META.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
                    className={`px-3 py-2 text-sm flex items-center gap-1.5 border-b-2 transition-colors ${tab === t.key ? 'border-[#E91E63] text-[#E91E63] font-semibold' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
                    data-testid={`mk-tab-${t.key}`}>
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'scorecard' && <ScorecardTab api={api} />}
      {tab === 'editorial' && <EditorialTab api={api} />}
      {tab === 'campaigns' && <CampaignsTab api={api} />}
      {tab === 'social' && <SocialTab api={api} />}
      {tab === 'assets' && <AssetsTab api={api} />}
      {tab === 'media' && <MediaTab api={api} />}
      {tab === 'partners' && <PartnersTab api={api} />}
      {tab === 'press' && <PressTab api={api} />}
      {tab === 'events' && <EventsTab api={api} />}
    </div>
  );
}

// ==================== 1. Scorecard ====================
function ScorecardTab({ api }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/marketing/scorecard').then(r => setData(r.data)).catch(() => toast.error('Load failed')); }, [api]);
  if (!data) return <Loading />;
  return (
    <div className="space-y-4" data-testid="mk-scorecard">
      <div className="grid md:grid-cols-4 gap-3">
        <Kpi label="Published this month" value={data.content.published_this_month} />
        <Kpi label="Content in progress" value={data.content.in_progress} />
        <Kpi label="Active campaigns" value={data.campaigns.active} />
        <Kpi label="Social scheduled" value={data.social.scheduled_this_month} />
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <Kpi label="Active partners" value={data.network.active_partners} />
        <Kpi label="Media contacts" value={data.network.media_contacts} />
        <Kpi label="Photos pending consent" value={data.compliance.media_pending_consent}
             variant={data.compliance.media_pending_consent > 0 ? 'warn' : 'default'} />
      </div>

      <Card><CardContent className="p-4">
        <div className="font-semibold mb-3 flex items-center gap-2"><Target className="h-4 w-4" /> Campaign progress</div>
        {data.campaigns.list.length === 0 ? <p className="text-sm text-gray-500">No active campaigns.</p> : (
          <div className="space-y-3">
            {data.campaigns.list.map(c => (
              <div key={c.id} data-testid={`mk-sc-campaign-${c.id}`}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium">{c.name}</span>
                  <span>{fmt$(c.raised)} / {fmt$(c.goal)} <span className="text-gray-500">({c.progress_pct}%)</span></span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                  <div className="h-2 bg-[#E91E63]" style={{ width: `${Math.min(100, c.progress_pct)}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent></Card>

      <Card><CardContent className="p-4">
        <div className="font-semibold mb-3 flex items-center gap-2"><PartyPopper className="h-4 w-4" /> Upcoming events</div>
        {data.events.upcoming.length === 0 ? <p className="text-sm text-gray-500">No upcoming events.</p> : (
          <ul className="text-sm space-y-1">
            {data.events.upcoming.map(e => (
              <li key={e.id} className="flex justify-between py-1">
                <span>{e.title} — {e.date}</span>
                <span className="text-gray-500">{e.rsvp_count} RSVPs</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent></Card>
    </div>
  );
}

// ==================== 2. Editorial ====================
function EditorialTab({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // null | 'new' | item
  const load = useCallback(() => {
    setLoading(true);
    api.get('/marketing/content').then(r => setItems(r.data)).catch(() => toast.error('Load failed')).finally(() => setLoading(false));
  }, [api]);
  useEffect(load, [load]);

  const byStatus = Object.fromEntries(CONTENT_STATUSES.map(s => [s, items.filter(i => i.status === s)]));

  const move = async (item, status) => {
    try { await api.put(`/marketing/content/${item.id}`, { status }); load(); toast.success(`Moved to ${status}`); }
    catch { toast.error('Move failed'); }
  };
  const del = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try { await api.delete(`/marketing/content/${item.id}`); load(); toast.success('Deleted'); }
    catch { toast.error('Delete failed'); }
  };

  if (loading) return <Loading />;
  return (
    <div data-testid="mk-editorial">
      <div className="flex justify-end mb-3">
        <Button onClick={() => setDialog('new')} data-testid="mk-content-new"><Plus className="h-4 w-4 mr-1" />New content</Button>
      </div>
      <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-2 overflow-x-auto">
        {CONTENT_STATUSES.map(s => (
          <div key={s} className="min-w-[190px]">
            <div className="text-xs uppercase font-semibold tracking-wider text-gray-500 mb-2 capitalize">{s} ({byStatus[s].length})</div>
            <div className="space-y-2">
              {byStatus[s].map(item => (
                <Card key={item.id} className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => setDialog(item)} data-testid={`mk-content-${item.id}`}>
                  <CardContent className="p-3">
                    <div className="text-sm font-medium">{item.title}</div>
                    <div className="text-xs text-gray-500 mt-1">{item.channel} · {item.due_date || 'no date'}</div>
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {CONTENT_STATUSES.filter(x => x !== s).slice(0, 3).map(x => (
                        <button key={x} onClick={(e) => { e.stopPropagation(); move(item, x); }}
                                className="text-[10px] px-1.5 py-0.5 border rounded hover:bg-gray-100">
                          → {x}
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
      {dialog && (
        <ContentDialog item={dialog === 'new' ? null : dialog} api={api}
                       onSaved={load} onClose={() => setDialog(null)} onDelete={del} />
      )}
    </div>
  );
}

function ContentDialog({ item, api, onSaved, onClose, onDelete }) {
  const [form, setForm] = useState(item || { title: '', channel: 'blog', status: 'idea', owner_email: '', due_date: '', publish_date: '', body: '', tags: [] });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      if (item) await api.put(`/marketing/content/${item.id}`, form);
      else await api.post('/marketing/content', form);
      toast.success(item ? 'Updated' : 'Created'); onSaved(); onClose();
    } catch { toast.error('Save failed'); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="mk-content-dialog">
        <DialogHeader><DialogTitle>{item ? 'Edit content' : 'New content'}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Title *</Label><Input value={form.title} onChange={e => set('title', e.target.value)} data-testid="mk-content-title" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Channel</Label>
              <select value={form.channel} onChange={e => set('channel', e.target.value)} className="w-full border rounded px-3 py-2">
                {CHANNELS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><Label>Status</Label>
              <select value={form.status} onChange={e => set('status', e.target.value)} className="w-full border rounded px-3 py-2">
                {CONTENT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Due date</Label><Input type="date" value={form.due_date || ''} onChange={e => set('due_date', e.target.value)} /></div>
            <div><Label>Publish date</Label><Input type="date" value={form.publish_date || ''} onChange={e => set('publish_date', e.target.value)} /></div>
          </div>
          <div><Label>Owner email</Label><Input value={form.owner_email} onChange={e => set('owner_email', e.target.value)} /></div>
          <div><Label>Notes / body</Label><Textarea value={form.body} onChange={e => set('body', e.target.value)} rows={5} /></div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {item && <Button variant="outline" className="text-red-600 border-red-300" onClick={() => { onDelete(item); onClose(); }}><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>}
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="mk-content-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== 3. Campaigns ====================
function CampaignsTab({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    api.get('/marketing/campaigns').then(r => setItems(r.data)).catch(() => toast.error('Load failed')).finally(() => setLoading(false));
  }, [api]);
  useEffect(load, [load]);
  if (loading) return <Loading />;
  return (
    <div data-testid="mk-campaigns">
      <div className="flex justify-end mb-3">
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="mk-campaign-new"><Plus className="h-4 w-4 mr-1" />New campaign</Button>
      </div>
      {items.length === 0 ? <EmptyState msg="No campaigns yet." /> : (
        <div className="grid gap-2">
          {items.map(c => (
            <Card key={c.id} className="cursor-pointer hover:shadow-md" onClick={() => { setEditing(c); setDialogOpen(true); }} data-testid={`mk-campaign-${c.id}`}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start flex-wrap gap-2">
                  <div>
                    <div className="font-semibold">{c.name}</div>
                    <div className="text-xs text-gray-500">{c.start_date || '—'} → {c.end_date || '—'} · {c.status}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">{fmt$(c.raised)} / {fmt$(c.goal_amount)}</div>
                    <div className="text-xs text-gray-500">{c.progress_pct}%</div>
                  </div>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5 mt-2 overflow-hidden">
                  <div className="h-1.5 bg-[#E91E63]" style={{ width: `${Math.min(100, c.progress_pct)}%` }} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {dialogOpen && (
        <CrudDialog title={editing ? 'Edit campaign' : 'New campaign'} api={api}
          endpoint="/marketing/campaigns" item={editing}
          fields={[
            { key: 'name', label: 'Name *', required: true },
            { key: 'description', label: 'Description', type: 'textarea' },
            { key: 'goal_amount', label: 'Goal $', type: 'number' },
            { key: 'start_date', label: 'Start date', type: 'date' },
            { key: 'end_date', label: 'End date', type: 'date' },
            { key: 'status', label: 'Status', type: 'select', options: ['planning', 'active', 'past', 'cancelled'] },
          ]}
          onSaved={() => { setDialogOpen(false); load(); }}
          onClose={() => setDialogOpen(false)} testidPrefix="mk-campaign" />
      )}
    </div>
  );
}

// ==================== 5. Brand Assets ====================
function AssetsTab({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const load = useCallback(() => {
    setLoading(true);
    api.get('/marketing/assets').then(r => setItems(r.data)).catch(() => toast.error('Load failed')).finally(() => setLoading(false));
  }, [api]);
  useEffect(load, [load]);

  const upload = async (file) => {
    if (!file) return;
    const name = window.prompt('Asset name?', file.name);
    if (!name) return;
    const type = window.prompt('Type (logo / one_pager / template / other)?', 'logo') || 'logo';
    const fd = new FormData(); fd.append('file', file); fd.append('name', name); fd.append('asset_type', type);
    setUploading(true);
    try { await api.post('/marketing/assets', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('Uploaded'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const download = async (a) => {
    const resp = await api.get(`/marketing/assets/${a.id}/download`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([resp.data]));
    const link = document.createElement('a'); link.href = url; link.download = a.file_name || a.name; link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const del = async (a) => {
    if (!window.confirm(`Delete ${a.name}?`)) return;
    await api.delete(`/marketing/assets/${a.id}`); toast.success('Deleted'); load();
  };

  if (loading) return <Loading />;
  return (
    <div data-testid="mk-assets">
      <div className="flex justify-end mb-3">
        <input ref={fileRef} type="file" hidden onChange={e => upload(e.target.files[0])} data-testid="mk-asset-file" />
        <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="mk-asset-upload">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1" />Upload asset</>}
        </Button>
      </div>
      {items.length === 0 ? <EmptyState msg="No brand assets yet." /> : (
        <div className="grid md:grid-cols-3 gap-3">
          {items.map(a => (
            <Card key={a.id} data-testid={`mk-asset-${a.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">{a.name}</div>
                    <div className="text-xs text-gray-500">{a.asset_type} · {(a.size_bytes / 1024).toFixed(1)} KB</div>
                    {a.description && <div className="text-xs text-gray-600 mt-1">{a.description}</div>}
                  </div>
                </div>
                <div className="flex gap-1 mt-3">
                  <Button size="sm" variant="outline" onClick={() => download(a)} data-testid={`mk-asset-dl-${a.id}`}><Download className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="outline" onClick={() => del(a)} className="text-red-600 border-red-300"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== 6. Media Library w/ Consent ====================
function MediaTab({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const load = useCallback(() => {
    setLoading(true);
    api.get(`/marketing/media${filter ? `?consent=${filter}` : ''}`).then(r => setItems(r.data)).catch(() => toast.error('Load failed')).finally(() => setLoading(false));
  }, [api, filter]);
  useEffect(load, [load]);

  const upload = async (file) => {
    if (!file) return;
    const desc = window.prompt('Description / caption?', '') || '';
    const names = window.prompt('Participants in photo (comma-separated, optional):', '') || '';
    const consent = window.prompt('Consent status? (consented / pending / restricted / expired)', 'pending') || 'pending';
    const fd = new FormData();
    fd.append('file', file); fd.append('description', desc); fd.append('participant_names', names); fd.append('consent_status', consent);
    setUploading(true);
    try { await api.post('/marketing/media', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('Uploaded'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); } finally { setUploading(false); }
  };

  const setConsent = async (m, status) => {
    try { await api.put(`/marketing/media/${m.id}/consent`, { consent_status: status }); toast.success('Consent updated'); load(); }
    catch { toast.error('Update failed'); }
  };
  const download = async (m) => {
    try {
      const resp = await api.get(`/marketing/media/${m.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data])); window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(e.response?.status === 403 ? 'Blocked — consent not granted' : 'Download failed'); }
  };
  const del = async (m) => {
    if (!window.confirm('Delete this media item?')) return;
    await api.delete(`/marketing/media/${m.id}`); toast.success('Deleted'); load();
  };

  const consentColor = { consented: 'bg-[#E8F5E9] text-[#1B5E20]', pending: 'bg-[#FFF3E0] text-[#E65100]', restricted: 'bg-[#FFEBEE] text-[#B71C1C]', expired: 'bg-gray-100 text-gray-600' };
  if (loading) return <Loading />;

  return (
    <div data-testid="mk-media">
      <div className="flex flex-wrap justify-between items-center mb-3 gap-2">
        <div className="flex gap-1">
          {['', 'consented', 'pending', 'restricted', 'expired'].map(c => (
            <button key={c || 'all'} onClick={() => setFilter(c)}
                    className={`px-3 py-1 rounded-full text-xs ${filter === c ? 'bg-[#E91E63] text-white' : 'bg-white border'}`}
                    data-testid={`mk-media-filter-${c || 'all'}`}>{c || 'All'}</button>
          ))}
        </div>
        <div>
          <input ref={fileRef} type="file" hidden accept="image/*,video/*" onChange={e => upload(e.target.files[0])} data-testid="mk-media-file" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="mk-media-upload">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1" />Upload media</>}
          </Button>
        </div>
      </div>
      {items.length === 0 ? <EmptyState msg="No media uploaded." /> : (
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map(m => (
            <Card key={m.id} data-testid={`mk-media-${m.id}`}>
              <CardContent className="p-3">
                <div className="aspect-video bg-gray-100 rounded flex items-center justify-center text-gray-400 mb-2">
                  <ImageIcon className="h-8 w-8" />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`${consentColor[m.consent_status]} border-0 text-xs`}>{m.consent_status}</Badge>
                  <span className="text-xs text-gray-500 truncate">{m.file_name}</span>
                </div>
                {m.description && <div className="text-xs text-gray-600 mt-1 line-clamp-2">{m.description}</div>}
                {m.participant_names?.length > 0 && (
                  <div className="text-[10px] text-gray-500 mt-1">👥 {m.participant_names.join(', ')}</div>
                )}
                <div className="flex flex-wrap gap-1 mt-2">
                  <Button size="sm" variant="outline" onClick={() => download(m)} data-testid={`mk-media-dl-${m.id}`}>
                    <Download className="h-3 w-3" />
                  </Button>
                  <select onChange={e => setConsent(m, e.target.value)} value={m.consent_status}
                          className="text-xs border rounded px-1" data-testid={`mk-media-consent-${m.id}`}>
                    {['consented', 'pending', 'restricted', 'expired'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <Button size="sm" variant="outline" onClick={() => del(m)} className="text-red-600 border-red-300">
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== 7. Partners ====================
function PartnersTab({ api }) {
  return <SimpleCrudTab api={api} endpoint="/marketing/partners" title="Partner" testid="mk-partners"
    fields={[
      { key: 'name', label: 'Name *', required: true },
      { key: 'org_type', label: 'Organization type', placeholder: 'school / medical / church / employer / ARC' },
      { key: 'contact_name', label: 'Contact person' },
      { key: 'email', label: 'Email', type: 'email' },
      { key: 'phone', label: 'Phone' },
      { key: 'relationship', label: 'Relationship', type: 'select', options: ['active', 'cultivating', 'dormant', 'referral_source', 'referral_target'] },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ]}
    renderItem={p => (
      <>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold">{p.name}</span>
          <Badge variant="outline" className="text-xs">{p.relationship}</Badge>
          {p.org_type && <Badge variant="outline" className="text-xs">{p.org_type}</Badge>}
        </div>
        <div className="text-xs text-gray-500 mt-0.5">{p.contact_name}{p.email ? ` · ${p.email}` : ''}{p.phone ? ` · ${p.phone}` : ''}</div>
      </>
    )} />;
}

// ==================== 8. Press ====================
function PressTab({ api }) {
  return <SimpleCrudTab api={api} endpoint="/marketing/media-contacts" title="Press contact" testid="mk-press"
    fields={[
      { key: 'name', label: 'Name *', required: true },
      { key: 'outlet', label: 'Outlet' },
      { key: 'beat', label: 'Beat / topic' },
      { key: 'email', label: 'Email', type: 'email' },
      { key: 'phone', label: 'Phone' },
      { key: 'last_interaction', label: 'Last interaction', type: 'date' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ]}
    renderItem={m => (
      <>
        <div className="font-semibold">{m.name}</div>
        <div className="text-xs text-gray-500">{m.outlet}{m.beat ? ` · ${m.beat}` : ''}{m.last_interaction ? ` · last ${m.last_interaction}` : ''}</div>
      </>
    )} />;
}

// ==================== 9. Events ====================
function EventsTab({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    api.get('/marketing/events').then(r => setItems(r.data)).catch(() => toast.error('Load failed')).finally(() => setLoading(false));
  }, [api]);
  useEffect(load, [load]);
  if (loading) return <Loading />;

  return (
    <div data-testid="mk-events">
      <div className="flex justify-end mb-3">
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="mk-event-new"><Plus className="h-4 w-4 mr-1" />New event</Button>
      </div>
      {items.length === 0 ? <EmptyState msg="No events yet." /> : (
        <div className="grid gap-2">
          {items.map(e => (
            <Card key={e.id} className="cursor-pointer hover:shadow-md" onClick={() => setSelected(e)} data-testid={`mk-event-${e.id}`}>
              <CardContent className="p-4 flex justify-between items-start flex-wrap gap-2">
                <div>
                  <div className="font-semibold">{e.title}</div>
                  <div className="text-xs text-gray-500">{e.date || 'no date'} · {e.venue || 'no venue'} · {e.status}</div>
                </div>
                <div className="text-right text-sm">
                  <div>{e.rsvp_count} RSVPs</div>
                  <div className="text-xs text-gray-500">Budget {fmt$(e.budget)} · Spent {fmt$(e.expenses)}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {dialogOpen && (
        <CrudDialog title={editing ? 'Edit event' : 'New event'} api={api}
          endpoint="/marketing/events" item={editing}
          fields={[
            { key: 'title', label: 'Title *', required: true },
            { key: 'date', label: 'Date', type: 'date' },
            { key: 'venue', label: 'Venue' },
            { key: 'description', label: 'Description', type: 'textarea' },
            { key: 'budget', label: 'Budget', type: 'number' },
            { key: 'expenses', label: 'Expenses', type: 'number' },
            { key: 'status', label: 'Status', type: 'select', options: ['planning', 'open_for_rsvp', 'sold_out', 'past', 'cancelled'] },
          ]}
          onSaved={() => { setDialogOpen(false); load(); }}
          onClose={() => setDialogOpen(false)} testidPrefix="mk-event" />
      )}
      {selected && <EventDetail event={selected} api={api} onClose={() => setSelected(null)} onReload={() => { load(); setSelected(null); }} />}
    </div>
  );
}

function EventDetail({ event, api, onClose, onReload }) {
  const [rsvp, setRsvp] = useState({ name: '', email: '', phone: '', guests: 1 });
  const [loading, setLoading] = useState(false);
  const addRsvp = async () => {
    if (!rsvp.name.trim()) { toast.error('Name required'); return; }
    setLoading(true);
    try { await api.post(`/marketing/events/${event.id}/rsvp`, rsvp); toast.success('RSVP added'); onReload(); }
    catch { toast.error('Failed'); } finally { setLoading(false); }
  };
  const removeRsvp = async (r) => {
    try { await api.delete(`/marketing/events/${event.id}/rsvp/${r.id}`); toast.success('Removed'); onReload(); }
    catch { toast.error('Failed'); }
  };
  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="mk-event-detail">
        <DialogHeader><DialogTitle>{event.title}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <div><span className="text-gray-500">Date:</span> {event.date} · <span className="text-gray-500">Venue:</span> {event.venue}</div>
          <div><span className="text-gray-500">Budget:</span> {fmt$(event.budget)} · <span className="text-gray-500">Spent:</span> {fmt$(event.expenses)}</div>
          {event.description && <div className="whitespace-pre-wrap">{event.description}</div>}
          <div className="border-t pt-3">
            <div className="font-semibold mb-2">RSVPs ({event.rsvp_count})</div>
            <div className="grid grid-cols-6 gap-1 items-end mb-3">
              <div className="col-span-2"><Label className="text-xs">Name</Label><Input value={rsvp.name} onChange={e => setRsvp({...rsvp, name: e.target.value})} data-testid="mk-rsvp-name" /></div>
              <div className="col-span-2"><Label className="text-xs">Email</Label><Input value={rsvp.email} onChange={e => setRsvp({...rsvp, email: e.target.value})} data-testid="mk-rsvp-email" /></div>
              <div><Label className="text-xs">Guests</Label><Input type="number" min="1" max="20" value={rsvp.guests} onChange={e => setRsvp({...rsvp, guests: parseInt(e.target.value)||1})} /></div>
              <Button onClick={addRsvp} disabled={loading} data-testid="mk-rsvp-add">Add</Button>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {(event.rsvps || []).map(r => (
                <div key={r.id} className="flex justify-between text-sm py-1 border-b">
                  <span>{r.name} <span className="text-gray-500">({r.guests})</span> · {r.email}</span>
                  <button onClick={() => removeRsvp(r)} className="text-red-600 text-xs">Remove</button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter><Button onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== Shared components ====================
function Loading() { return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#E91E63]" /></div>; }
function EmptyState({ msg }) { return <Card><CardContent className="p-12 text-center text-gray-500">{msg}</CardContent></Card>; }
function Kpi({ label, value, variant }) {
  return (
    <Card className={variant === 'warn' ? 'border-orange-300 bg-orange-50' : ''}>
      <CardContent className="p-4">
        <div className="text-xs text-gray-500 uppercase mb-1">{label}</div>
        <div className="text-2xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}

function SimpleCrudTab({ api, endpoint, title, fields, renderItem, testid }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    api.get(endpoint).then(r => setItems(r.data)).catch(() => toast.error('Load failed')).finally(() => setLoading(false));
  }, [api, endpoint]);
  useEffect(load, [load]);
  if (loading) return <Loading />;
  return (
    <div data-testid={testid}>
      <div className="flex justify-end mb-3">
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid={`${testid}-new`}><Plus className="h-4 w-4 mr-1" />New {title.toLowerCase()}</Button>
      </div>
      {items.length === 0 ? <EmptyState msg={`No ${title.toLowerCase()}s yet.`} /> : (
        <div className="grid gap-2">
          {items.map(item => (
            <Card key={item.id} className="cursor-pointer hover:shadow-md" onClick={() => { setEditing(item); setOpen(true); }} data-testid={`${testid}-${item.id}`}>
              <CardContent className="p-3">{renderItem(item)}</CardContent>
            </Card>
          ))}
        </div>
      )}
      {open && (
        <CrudDialog title={editing ? `Edit ${title.toLowerCase()}` : `New ${title.toLowerCase()}`} api={api}
          endpoint={endpoint} item={editing} fields={fields}
          onSaved={() => { setOpen(false); load(); }}
          onClose={() => setOpen(false)} testidPrefix={testid} />
      )}
    </div>
  );
}

function CrudDialog({ title, api, endpoint, item, fields, onSaved, onClose, testidPrefix, initialValues }) {
  const [form, setForm] = useState(
    item || initialValues || Object.fromEntries(fields.map(f => [f.key, f.type === 'number' ? 0 : '']))
  );
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    for (const f of fields) {
      if (f.required && !String(form[f.key] || '').trim()) { toast.error(`${f.label} required`); return; }
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // numeric coercion
      for (const f of fields) {
        if (f.type === 'number') payload[f.key] = parseFloat(payload[f.key]) || 0;
      }
      if (item) await api.put(`${endpoint}/${item.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success(item ? 'Updated' : 'Created'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };
  const del = async () => {
    if (!item || !window.confirm(`Delete?`)) return;
    try { await api.delete(`${endpoint}/${item.id}`); toast.success('Deleted'); onSaved(); }
    catch { toast.error('Delete failed'); }
  };
  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`${testidPrefix}-dialog`}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          {fields.map(f => (
            <div key={f.key}>
              <Label>{f.label}</Label>
              {f.type === 'textarea' ? (
                <Textarea value={form[f.key] || ''} onChange={e => set(f.key, e.target.value)} rows={3}
                          data-testid={`${testidPrefix}-${f.key}`} />
              ) : f.type === 'select' ? (
                <select value={form[f.key] || ''} onChange={e => set(f.key, e.target.value)}
                        className="w-full border rounded px-3 py-2" data-testid={`${testidPrefix}-${f.key}`}>
                  <option value="">—</option>
                  {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <Input type={f.type || 'text'} value={form[f.key] || ''}
                       onChange={e => set(f.key, e.target.value)}
                       placeholder={f.placeholder} data-testid={`${testidPrefix}-${f.key}`} />
              )}
            </div>
          ))}
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {item && <Button variant="outline" onClick={del} className="text-red-600 border-red-300"><Trash2 className="h-4 w-4 mr-1" />Delete</Button>}
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid={`${testidPrefix}-save`}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
